import { useRef, useEffect, useState } from "react";
import gsap from "gsap";
import * as StyledMedia from "./Styled.Media";
// Hero images: 1x and 2x for each breakpoint
import hero384 from "@/assets/images/website-header-bg-384.webp";
import hero384_2x from "@/assets/images/website-header-bg-384-2x.webp";
import hero568 from "@/assets/images/website-header-bg-568.webp";
import hero568_2x from "@/assets/images/website-header-bg-568-2x.webp";
import hero852 from "@/assets/images/website-header-bg-852.webp";
import hero852_2x from "@/assets/images/website-header-bg-852-2x.webp";
import hero1136 from "@/assets/images/website-header-bg-1136.webp";
import hero1136_2x from "@/assets/images/website-header-bg-1136-2x.webp";
import hero1720 from "@/assets/images/website-header-bg-1720.webp";
import hero1720_2x from "@/assets/images/website-header-bg-1720-2x.webp";
import rippedPaper384 from "@/assets/images/website-header-ripped-paper-384.webp";
import rippedPaper384_2x from "@/assets/images/website-header-ripped-paper-384-2x.webp";
import rippedPaper568 from "@/assets/images/website-header-ripped-paper-568.webp";
import rippedPaper568_2x from "@/assets/images/website-header-ripped-paper-568-2x.webp";
import rippedPaper852 from "@/assets/images/website-header-ripped-paper-852.webp";
import rippedPaper852_2x from "@/assets/images/website-header-ripped-paper-852-2x.webp";
import rippedPaper1136 from "@/assets/images/website-header-ripped-paper-1136.webp";
import rippedPaper1136_2x from "@/assets/images/website-header-ripped-paper-1136-2x.webp";
import rippedPaper1720 from "@/assets/images/website-header-ripped-paper-1720.webp";
import rippedPaper1720_2x from "@/assets/images/website-header-ripped-paper-1720-2x.webp";

// Hero image data
// const heroImages = [
//   { w: 384, src1x: hero384, src2x: hero384_2x, h: 496 },
//   { w: 568, src1x: hero568, src2x: hero568_2x, h: 734 },
//   { w: 852, src1x: hero852, src2x: hero852_2x, h: 1101 },
//   { w: 1136, src1x: hero1136, src2x: hero1136_2x, h: 1469 },
//   { w: 1720, src1x: hero1720, src2x: hero1720_2x, h: 2224 },
// ];
const heroImages = [
  { w: 384, src: hero384, h: 496 },
  { w: 568, src: hero568, h: 734 },
  { w: 852, src: hero852, h: 1101 },
  { w: 1136, src: hero1136, h: 1469 },
  { w: 1720, src: hero1720, h: 2224 },
];

// Ripped paper data
// const paperImages = [
//   { w: 384, src1x: rippedPaper384, src2x: rippedPaper384_2x, h: 88 },
//   { w: 568, src1x: rippedPaper568, src2x: rippedPaper568_2x, h: 130 },
//   { w: 852, src1x: rippedPaper852, src2x: rippedPaper852_2x, h: 196 },
//   { w: 1136, src1x: rippedPaper1136, src2x: rippedPaper1136_2x, h: 261 },
//   { w: 1720, src1x: rippedPaper1720, src2x: rippedPaper1720_2x, h: 398 },
// ];
const paperImages = [
  { w: 384, src: rippedPaper384, h: 88 },
  { w: 568, src: rippedPaper568, h: 130 },
  { w: 852, src: rippedPaper852, h: 196 },
  { w: 1136, src: rippedPaper1136, h: 261 },
  { w: 1720, src: rippedPaper1720, h: 398 },
];

const Hero = ({ title }) => {
  const heroRef = useRef(null);
  const revealRef = useRef(null);
  const panelRef = useRef(null);
  const paperRef = useRef(null);
  const titleRef = useRef(null);

  const [isReady, setIsReady] = useState(false);
  const hasAnimated = useRef(false);

  const animate = (height) => {
    // Ensure panel is visible
    panelRef.current.style.height = `${height}px`;

    // Initial styles
    gsap.set(revealRef.current, { y: 0 });
    gsap.set(titleRef.current, { opacity: 0, y: 30 });

    const tl = gsap.timeline();

    // Animate cover (panel + ripped paper)
    tl.to(revealRef.current, {
      y: height,
      duration: 1.2,
      ease: "power2.inOut",
    });

    // Fade in title
    tl.to(
      titleRef.current,
      { opacity: 1, y: 0, duration: 0.8, ease: "power2.out" },
      "-=0.6"
    );

    setIsReady(true);
  };

  useEffect(() => {
    const heroEl = heroRef.current;
    if (!heroEl || hasAnimated.current) return;

    const height = heroEl.getBoundingClientRect().height;

    animate(height);
    hasAnimated.current = true;
  }, []);

  // ✅ ADD: Enhanced debugging to see what's really happening
  // useEffect(() => {
  //   const img = heroRef.current?.querySelector("img");
  //   if (img) {
  //     const logImageInfo = () => {
  //       console.log({
  //         currentSrc: img.currentSrc,
  //         elementWidth: img.width,
  //         clientWidth: img.clientWidth,
  //         offsetWidth: img.offsetWidth,
  //         naturalWidth: img.naturalWidth,
  //         naturalHeight: img.naturalHeight,
  //         sizes: img.sizes,
  //         srcset: img.srcset, // ✅ ADD: See actual srcset
  //         viewport: window.innerWidth, // ✅ ADD: Current viewport
  //       });
  //     };

  //     img.addEventListener("load", logImageInfo);
  //     window.addEventListener("resize", logImageInfo);

  //     return () => {
  //       img.removeEventListener("load", logImageInfo);
  //       window.removeEventListener("resize", logImageInfo);
  //     };
  //   }
  // }, []);

  const heroSrcSet = heroImages.map((img) => `${img.src} ${img.w}w`).join(", ");
  const paperSrcSet = paperImages
    .map((img) => `${img.src} ${img.w}w`)
    .join(", ");

  // ✅ Conservative sizes to force smaller image selection
  const heroSizes = `
    (max-width: 400px) calc(100vw -16px),
    (max-width: 600px) calc(100vw - 32px),
    (max-width: 900px) calc(100vw - 48px),
    (max-width: 1200px) calc(100vw - 64px),
    (max-width: 1800px) calc(100vw - 80px),
    1720px
  `;

  return (
    <StyledMedia.HeroWrapper ref={heroRef} $hidden={!isReady}>
      <StyledMedia.HeroGridWrapper>
        <picture>
          <source type="image/webp" srcSet={heroSrcSet} sizes={heroSizes} />
          <img
            src={heroImages[0].src} // fallback to smallest
            srcSet={heroSrcSet}
            sizes={heroSizes}
            width={1720}
            height={2224}
            style={{
              aspectRatio: "1720 / 2224",
              width: "100%",
              height: "auto",
            }}
            alt="Hero Background"
            fetchPriority="high"
            decoding="async"
            loading="eager"
          />
        </picture>
      </StyledMedia.HeroGridWrapper>

      <StyledMedia.CatchDisplayWrapper>
        <StyledMedia.CatchDisplay as="h1" ref={titleRef}>
          {title}
        </StyledMedia.CatchDisplay>
      </StyledMedia.CatchDisplayWrapper>

      <StyledMedia.HeroRevealWrapper ref={revealRef}>
        <StyledMedia.HeroRippedPaperWrapper ref={paperRef}>
          <picture>
            <source type="image/webp" srcSet={paperSrcSet} sizes={heroSizes} />
            <img
              src={paperImages[0].src} // fallback to smallest
              srcSet={paperSrcSet}
              sizes={heroSizes}
              width={1720}
              height={398}
              style={{
                aspectRatio: "1720 / 398",
                width: "100%",
                height: "auto",
              }}
              alt="Ripped Paper"
              fetchPriority="high"
              decoding="async"
              loading="lazy"
            />
          </picture>
        </StyledMedia.HeroRippedPaperWrapper>
        <StyledMedia.HeroRevealPanel ref={panelRef} />
      </StyledMedia.HeroRevealWrapper>
    </StyledMedia.HeroWrapper>
  );
};

export default Hero;
